import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Button, Dialog, TextField, Alert, IconButton, Grid, MenuItem, Typography, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import { PageHeader } from '../../components/ui/PageHeader';
import { getPageSubtitle } from '../../constants/pageSubtitles';
import { superAdminAPI } from '../../services/api';
import {
  SuperAdminDataTable, apiErrorMessage, statusChip, AdminTableEditButton, STATUS_FILTER_OPTIONS,
} from '../../components/superadmin/SuperAdminDataTable';
import { SuperAdminDetailDrawer, SuperAdminDetailField } from '../../components/superadmin/SuperAdminDetailDrawer';
import { RADIUS } from '../../theme/appTheme';
import { catchyPrimaryButtonSx, catchyTextFieldSx } from '../../theme/formStyles';

const TITLES: Record<string, string> = {
  vehicle_type: 'Vehicle Types',
  vehicle_make: 'Vehicle Makes',
  vehicle_model: 'Vehicle Models',
};

const defaultForm = { name: '', code: '', status: 'active', parentId: '', vehicleTypeId: '' };

export const MasterDataPage: React.FC = () => {
  const { category = 'vehicle_type' } = useParams<{ category: string }>();
  const [items, setItems] = useState<any[]>([]);
  const [makes, setMakes] = useState<any[]>([]);
  const [types, setTypes] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState(defaultForm);
  const [detailRow, setDetailRow] = useState<any>(null);
  const [message, setMessage] = useState('');

  const loadLookups = async () => {
    try {
      const [makeRes, typeRes] = await Promise.all([
        superAdminAPI.getMasterData('vehicle_make'),
        superAdminAPI.getMasterData('vehicle_type'),
      ]);
      setMakes(makeRes.data || []);
      setTypes(typeRes.data || []);
    } catch {
      setMakes([]);
      setTypes([]);
    }
  };

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await superAdminAPI.getMasterData(category);
      setItems(res.data);
      if (category === 'vehicle_model' || category === 'vehicle_make') {
        await loadLookups();
      }
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load master data'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [category]);

  const openCreate = () => {
    setEditId(null);
    setForm(defaultForm);
    setOpen(true);
  };

  const openEdit = (item: any) => {
    setEditId(item._id);
    setForm({
      name: item.name || '',
      code: item.code || '',
      status: item.status || 'active',
      parentId: item.parentId ? String(item.parentId) : '',
      vehicleTypeId: item.vehicleTypeId ? String(item.vehicleTypeId) : '',
    });
    setOpen(true);
  };

  const handleSave = async () => {
    try {
      if (category === 'vehicle_model' && (!form.parentId || !form.vehicleTypeId)) {
        setError('Select make and vehicle type for this model');
        return;
      }
      const payload = {
        name: form.name,
        code: form.code,
        status: form.status,
        parentId: form.parentId || null,
        vehicleTypeId: form.vehicleTypeId || null,
      };
      if (editId) {
        await superAdminAPI.updateMasterData(category, editId, payload);
        setMessage('Record updated');
      } else {
        await superAdminAPI.createMasterData(category, payload);
        setMessage('Record created');
      }
      setOpen(false);
      setEditId(null);
      setForm(defaultForm);
      load();
    } catch (err) {
      setError(apiErrorMessage(err, editId ? 'Update failed' : 'Create failed'));
    }
  };

  const makeName = (id?: string) => makes.find((m) => String(m._id) === String(id))?.name || '—';
  const typeName = (id?: string) => types.find((t) => String(t._id) === String(id))?.name || '—';

  const buildMasterFields = (item: any): SuperAdminDetailField[] => [
    { label: 'Name', value: item.name },
    { label: 'Code', value: item.code || '—' },
    { label: 'Category', value: TITLES[category] || category },
    ...(category === 'vehicle_model' ? [
      { label: 'Make', value: makeName(item.parentId) },
      { label: 'Vehicle Type', value: typeName(item.vehicleTypeId) },
    ] : []),
    { label: 'Record ID', value: String(item._id || ''), fullWidth: true },
  ];

  return (
    <Box>
      <PageHeader title={TITLES[category] || 'Master Data'} subtitle={getPageSubtitle(`master_${category}`)}
        icon={<DirectionsCarIcon />}

        action={
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={catchyPrimaryButtonSx}>
            Add New
          </Button>
        }
      />
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

      <SuperAdminDataTable
        rows={items}
        loading={loading}
        error={error}
        exportFileName={`super-admin-master-${category}`}
        exportSheetName={TITLES[category] || category}
        statusField="status"
        searchPlaceholder="Search name or code..."
        columns={[
          {
            id: 'name',
            header: 'Name',
            searchValue: (i) => i.name,
            exportValue: (i) => i.name,
            render: (i) => <strong>{i.name}</strong>,
          },
          {
            id: 'code',
            header: 'Code',
            searchValue: (i) => i.code,
            exportValue: (i) => i.code,
            render: (i) => i.code || '—',
          },
          ...(category === 'vehicle_model' ? [
            {
              id: 'make',
              header: 'Make',
              searchValue: (i: any) => makeName(i.parentId),
              exportValue: (i: any) => makeName(i.parentId),
              render: (i: any) => makeName(i.parentId),
            },
            {
              id: 'type',
              header: 'Vehicle Type',
              searchValue: (i: any) => typeName(i.vehicleTypeId),
              exportValue: (i: any) => typeName(i.vehicleTypeId),
              render: (i: any) => typeName(i.vehicleTypeId),
            },
          ] : []),
          {
            id: 'status',
            header: 'Status',
            exportValue: (i) => i.status,
            filterType: 'select',
            filterOptions: STATUS_FILTER_OPTIONS,
            filterValue: (i) => i.status,
            render: (i) => statusChip(i.status),
          },
        ]}
        onRowClick={setDetailRow}
        renderActions={(item) => (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
            <AdminTableEditButton onClick={() => openEdit(item)} />
            <Tooltip title="Delete">
              <IconButton size="small" onClick={async (e) => {
                e.stopPropagation();
                await superAdminAPI.deleteMasterData(category, item._id);
                load();
              }}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      />

      <SuperAdminDetailDrawer
        open={Boolean(detailRow)}
        onClose={() => setDetailRow(null)}
        moduleTitle={TITLES[category] || 'Master Data'}
        title={detailRow?.name || ''}
        subtitle={detailRow?.code}
        status={detailRow?.status}
        fields={detailRow ? buildMasterFields(detailRow) : []}
        primaryActions={detailRow ? (
          <Button variant="contained" onClick={() => { openEdit(detailRow); setDetailRow(null); }} sx={catchyPrimaryButtonSx}>
            Edit
          </Button>
        ) : undefined}
      />

      <Dialog open={open} onClose={() => setOpen(false)} PaperProps={{ sx: { borderRadius: `${RADIUS}px`, p: 3, minWidth: 400 } }}>
        <Typography variant="h6" fontWeight={700} mb={2}>
          {editId ? 'Edit' : 'Add'} {TITLES[category]}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}><TextField label="Name" fullWidth size="small" sx={catchyTextFieldSx} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Grid>
          <Grid item xs={12}><TextField label="Code" fullWidth size="small" sx={catchyTextFieldSx} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} /></Grid>
          {category === 'vehicle_model' && (
            <>
              <Grid item xs={12}>
                <TextField select label="Make" required fullWidth size="small" sx={catchyTextFieldSx} value={form.parentId} onChange={(e) => setForm({ ...form, parentId: e.target.value })}>
                  {makes.map((m) => (
                    <MenuItem key={m._id} value={m._id}>{m.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField select label="Vehicle Type" required fullWidth size="small" sx={catchyTextFieldSx} value={form.vehicleTypeId} onChange={(e) => setForm({ ...form, vehicleTypeId: e.target.value })}>
                  {types.map((t) => (
                    <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <TextField select label="Status" fullWidth size="small" sx={catchyTextFieldSx} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12}><Button variant="contained" fullWidth onClick={handleSave} sx={catchyPrimaryButtonSx}>Save</Button></Grid>
        </Grid>
      </Dialog>
    </Box>
  );
};
